import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { createCliente } from '../api/clienti';
import { apiErrorMessage } from '../lib/apiError';
import { clienteCreateSchema, type ClienteCreateData } from '../schemas/cliente';
import ClienteFormFields from '../components/ClienteFormFields';

export default function CreaClientePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ClienteCreateData>({
    resolver: zodResolver(clienteCreateSchema),
    defaultValues: {
      ragione_sociale: '',
      piva: '',
      codice_fiscale: '',
      indirizzo: '',
      cap: '',
      citta: '',
      provincia: '',
      nazione: 'IT',
      // FIX 2 (R6-D): vuoto, non '0000000'. La sentinella sembrava un valore
      // reale ma la validazione la conta come assente, e l'utente non poteva
      // capire perche' un'azienda senza PEC venisse bloccata.
      codice_destinatario: '',
      referente: '',
      telefono: '',
      email: '',
      pec: '',
    },
  });

  const onSubmit = async (data: ClienteCreateData) => {
    setIsSaving(true);
    setGlobalError(null);
    try {
      const created = await createCliente({
        ragione_sociale: data.ragione_sociale,
        piva: data.piva?.trim() || undefined,
        codice_fiscale: data.codice_fiscale?.trim() || undefined,
        indirizzo: data.indirizzo?.trim() || undefined,
        cap: data.cap?.trim() || undefined,
        citta: data.citta?.trim() || undefined,
        provincia: data.provincia?.trim() || undefined,
        nazione: data.nazione?.trim() || undefined,
        codice_destinatario: data.codice_destinatario?.trim() || undefined,
        referente: data.referente?.trim() || undefined,
        telefono: data.telefono?.trim() || undefined,
        email: data.email,
        pec: data.pec?.trim() || undefined,
      });
      navigate(`/clienti/${created.id}`);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { detail?: string } } })?.response?.status;
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      if (status === 409) {
        if (detail?.toLowerCase().includes('piva') || detail?.includes('P.IVA')) {
          setError('piva', { message: detail ?? t('anagrafiche.crea_cliente.piva_esistente') });
        } else if (detail?.toLowerCase().includes('codice fiscale') || detail?.toLowerCase().includes('cf')) {
          setError('codice_fiscale', { message: detail ?? t('anagrafiche.crea_cliente.cf_esistente') });
        } else {
          setGlobalError(detail ?? t('anagrafiche.crea_cliente.cliente_esistente'));
        }
      } else {
        // Il 422 di validazione (es. checksum del CF) ha un detail parlante —
        // stringa o array Pydantic {loc,msg}: `apiErrorMessage` lo estrae
        // (equivalente in-app del `messaggio422()` della landing). Scartarlo
        // mostrava il generico «Errore durante il salvataggio» al posto del
        // messaggio del validatore.
        setGlobalError(apiErrorMessage(err, t('anagrafiche.crea_cliente.errore_salvataggio')));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 text-sm mb-6">
          <Link to="/clienti" className="text-sage hover:underline flex items-center gap-1">
            <ArrowLeft size={14} />
            {t('anagrafiche.clienti.titolo')}
          </Link>
          <span className="text-ink-faint">/</span>
          <span className="font-semibold text-ink-dark">{t('anagrafiche.crea_cliente.titolo')}</span>
        </div>

        <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-mid bg-stone-bg">
            <h1 className="text-base font-semibold text-ink-dark">{t('anagrafiche.crea_cliente.titolo')}</h1>
            <p className="text-xs text-ink-soft mt-0.5">{t('anagrafiche.crea_cliente.sottotitolo')}</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-4">
            {globalError && (
              <div className="mb-4 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded-md text-sm text-accent-danger">
                {globalError}
              </div>
            )}

            <ClienteFormFields register={register} errors={errors} />

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-stone-mid">
              <button
                type="button"
                onClick={() => navigate('/clienti')}
                className="px-4 py-2 text-sm text-ink-mid border border-stone-border rounded-lg hover:bg-stone-bg transition-colors"
              >
                {t('common.azioni.annulla')}
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-5 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60"
              >
                {isSaving ? t('anagrafiche.crea_cliente.salvataggio') : t('anagrafiche.crea_cliente.crea')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
